import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FaStar, FaCalendarAlt, FaClock, FaPlay, FaTv } from 'react-icons/fa';
import axios from 'axios';
import AppDownloadBanner from './AppDownloadBanner';
import './PublicMovieDetails.css';

function PublicMovieDetails() {
  const { media_type, film_id } = useParams();
  const [details, setDetails] = useState(null);
  const [cast, setCast] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMediaDetails = async () => {
      try {
        setLoading(true);
        
        // Créer une instance axios sans withCredentials pour les appels publics
        const publicAxios = axios.create({
          withCredentials: false,
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });

        // Récupérer les détails du média (appel public)
        const [detailsResponse, castResponse, platformsResponse] = await Promise.allSettled([
          publicAxios.get(`/api/fetch_details_from_id?media_type=${media_type}&id=${film_id}`),
          publicAxios.get(`/api/get-casting?movie_id=${film_id}&media_type=${media_type}`).catch(() => ({ data: [] })),
          publicAxios.get(`/api/get_streaming_platforms?media_type=${media_type}&id=${film_id}`).catch(() => ({ data: [] }))
        ]);

        if (detailsResponse.status === 'fulfilled') {
          setDetails(detailsResponse.value.data);
        } else {
          throw new Error('Impossible de récupérer les détails du média');
        }

        if (castResponse.status === 'fulfilled') {
          setCast(castResponse.value.data || []);
        }

        if (platformsResponse.status === 'fulfilled') {
          setPlatforms(platformsResponse.value.data || []);
        }

      } catch (error) {
        console.error('Error fetching media details:', error);
        setError('Impossible de charger les détails de ce média');
      } finally {
        setLoading(false);
      }
    };

    if (media_type && film_id) {
      fetchMediaDetails();
    }
  }, [media_type, film_id]);

  // Fonctions simples de détection de plateforme
  const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = () => /Android/.test(navigator.userAgent);
  const isDesktop = () => !isIOS() && !isAndroid();

  // Rendu du bouton selon la plateforme
  const renderAppButton = () => {
    if (isIOS() || isAndroid()) {
      return (
        <a href={`cinemapp://${media_type}/${film_id}`} className="prominent-app-button">
          📱 Ouvrir dans CinéList
        </a>
      );
    } else if (isDesktop()) {
      return (
        <button 
          className="prominent-app-button desktop-download" 
          onClick={() => {
            alert('📱 Téléchargez CinéList sur votre mobile !\n\n🍎 iOS: App Store\n🤖 Android: Play Store');
          }}
        >
          📲 Télécharger CinéList
        </button>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="public-movie-page">
        <div className="loading-container">
          <h2>Chargement...</h2>
        </div>
      </div>
    );
  }

  if (error || !details) {
    return (
      <div className="public-movie-page">
        <AppDownloadBanner mediaType={media_type} filmId={film_id} />
        <div className="error-container">
          <h2>Média non disponible</h2>
          <p>{error || 'Ce média n\'est pas accessible.'}</p>
          <div className="download-suggestion">
            <h3>Voir ce {media_type === 'movie' ? 'film' : 'série'} dans l'app CinéList</h3>
            <p>Téléchargez l'app pour accéder aux détails complets, noter et partager vos opinions !</p>
          </div>
        </div>
      </div>
    );
  }

  const year = details.release_date 
    ? new Date(details.release_date).getFullYear()
    : details.first_air_date 
    ? new Date(details.first_air_date).getFullYear()
    : null;

  return (
    <div className="public-movie-page">
      <Helmet>
        <meta 
          name="apple-itunes-app" 
          content={`app-id=6740208004, app-argument=https://films.ahkapp.fr/${media_type}/${film_id}`} 
        />
        <meta 
          property="og:title" 
          content={details ? `${details.title || details.name} - CinéList` : 'CinéList'} 
        />
        <meta 
          property="og:description" 
          content={details ? details.overview || 'Découvrez ce contenu sur CinéList' : 'Films & Séries entre amis'} 
        />
        <meta 
          property="og:image" 
          content={details && details.poster_path 
            ? `https://image.tmdb.org/t/p/w500${details.poster_path}` 
            : 'https://films.ahkapp.fr/logo512.png'
          } 
        />
        <meta 
          property="og:image:width" 
          content="500" 
        />
        <meta 
          property="og:image:height" 
          content="750" 
        />
        <meta 
          property="og:image:type" 
          content="image/jpeg" 
        />
        <meta 
          property="og:url" 
          content={`https://films.ahkapp.fr/${media_type}/${film_id}`} 
        />
        <meta 
          property="og:type" 
          content="video.movie" 
        />
      </Helmet>
      
      <AppDownloadBanner mediaType={media_type} filmId={film_id} />
      
      <div className="movie-hero">
        <div className="movie-poster">
          {details.poster_path ? (
            <img 
              src={`https://image.tmdb.org/t/p/w500${details.poster_path}`}
              alt={details.title || details.name}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = 'https://via.placeholder.com/500x750?text=Pas+d%27image';
              }}
            />
          ) : (
            <div className="placeholder-poster">
              <FaTv size={60} />
              <p>Pas d'image</p>
            </div>
          )}
        </div>

        {/* Section bouton central entre poster et titre */}
        <div className="app-button-section">
          {renderAppButton()}
        </div>

        <div className="movie-info">
          <h1>{details.title || details.name}</h1>
          
          <div className="movie-meta">
            {year && (
              <div className="meta-item">
                <FaCalendarAlt />
                <span>{year}</span>
              </div>
            )}
            
            {details.runtime && (
              <div className="meta-item">
                <FaClock />
                <span>{details.runtime} min</span>
              </div>
            )}
            
            {details.vote_average && (
              <div className="meta-item rating">
                <FaStar />
                <span>{details.vote_average.toFixed(1)}/10</span>
              </div>
            )}
          </div>

          {details.genres && details.genres.length > 0 && (
            <div className="genres">
              {details.genres.map(genre => (
                <span key={genre.id} className="genre-tag">
                  {genre.name}
                </span>
              ))}
            </div>
          )}

          <div className="overview">
            <h3>Synopsis</h3>
            <p>{details.overview || 'Pas de synopsis disponible.'}</p>
          </div>
        </div>
      </div>

      {cast.length > 0 && (
        <div className="cast-section">
          <h3>Casting principal</h3>
          <div className="cast-grid">
            {cast.slice(0, 6).map(actor => (
              <div key={actor.id} className="cast-member">
                <img 
                  src={actor.profile_path 
                    ? `https://image.tmdb.org/t/p/w185${actor.profile_path}`
                    : 'https://via.placeholder.com/185x278?text=Pas+de+photo'
                  } 
                  alt={actor.name}
                />
                <div className="cast-info">
                  <span className="actor-name">{actor.name}</span>
                  <span className="character-name">{actor.character}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {platforms.length > 0 && (
        <div className="platforms-section">
          <h3>Disponible sur</h3>
          <div className="platforms-grid">
            {platforms.map((platform, index) => (
              <div key={index} className="platform-item">
                <img src={platform.logo} alt={platform.name} />
                <span>{platform.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}


      <div className="app-promotion-section">
        <div className="app-promo-card">
          <h3>📱 Découvrez plus sur CinéList !</h3>
          <div className="promo-features">
            <div className="promo-feature">
              <FaStar /> Notez et créez vos listes
            </div>
            <div className="promo-feature">
              <FaPlay /> Trouvez où regarder
            </div>
            <div className="promo-feature">
              <FaTv /> Suivez vos amis et leurs goûts
            </div>
          </div>
          <p>
            Téléchargez l'app CinéList pour noter ce {media_type === 'movie' ? 'film' : 'série'}, 
            voir les avis de vos amis et obtenir des recommandations personnalisées !
          </p>
        </div>
      </div>

      <div className="limited-access-notice">
        <h4>Version web limitée</h4>
        <p>
          Cette page web ne montre qu'un aperçu. Pour noter, commenter, 
          voir les avis de vos amis et accéder à toutes les fonctionnalités, 
          téléchargez l'app mobile CinéList.
        </p>
      </div>
    </div>
  );
}

export default PublicMovieDetails;